// Externe Bibliotheken
import React, { Component } from 'react';
import { NavLink } from 'react-router';
import axios from 'axios';

// Interne Module
import * as storage from '@/_helper/storage';
import { paymentAlertText } from '@/_data/contentText';
import { convertPrice } from '@/_helper/mapper';
import { checkIsAllowToBookService } from '@/_helper/checkAccess';
import tracking from '@/_helper/tracking';
import { buildPostRequest } from '@/_helper/apiHelper';

// Komponenten
import Header from '@/comp/header/header';
import Footer from '@/comp/footer/Footer';
import Button from '@/comp/button/button.tsx';
import PayPal from '@/comp/payment/paymentCard/types/PayPal';
import CreditCard from '@/comp/payment/paymentCard/types/CreditCard';
import DirectDebit from '@/comp/payment/paymentCard/types/DirectDebit';
import StatusPage from '@/comp/statusPage/statusPage';
import InfoCard from '@/comp/ui/infoCard/infoCard';

// Styles
import './payment.scss';

// Assets
import masterCardSvg from '@/assets/img/payment/mastercard.svg';
import visaSvg from '@/assets/img/payment/visa.svg';
import amexSvg from '@/assets/img/payment/amex.svg';
import payPalSvg from '@/assets/img/payment/paypal.svg';
import { AppContext } from '@/context/AppContext.tsx';
import { Icon } from '@/comp/icon/icon.tsx';

const params = {
    paymentSteps: {
        deposit: {
            title: 'Anzahlung',
            action: 'startPayment',
        },
        final: {
            title: 'Restzahlung',
            action: 'finalPayment',
        },
        single: {
            title: 'Zahlung',
            action: 'finalPayment',
        },
    },
    paymentTypes: {
        creditCard: {
            allowed: false,
            title: 'Kreditkarte',
            className: 'credit-card',
        },
        directDebit: {
            allowed: false,
            title: 'Lastschrift',
            className: 'direct-debit',
        },
        payPal: {
            allowed: false,
            title: 'PayPal',
            className: 'paypal',
        },
    },
};

class Index extends Component {
    static contextType = AppContext;

    constructor(props) {
        super(props);

        this.state = {
            acceptAgb: false,
            bookingData: storage.get('bookingData') ?? null,
            pageState: 'initial',
            formValid: false,
            formVacancy: false,
            alreadyTested: false,
            paymentStep: undefined,
            paymentData: {},
            paypalInitSuccess: false,
        };

        this.paymentCard = this.paymentCard.bind(this);
        this.paymentChange = this.paymentChange.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    }

    componentDidMount() {
        document.title = 'Zahlungen tätigen' + window.$documentTitle;
        document.querySelector('html').classList.add('redesign');
        tracking('loaded', 'payment');

        const bookingParams = storage.get('bookingData')?.bookingParams;
        const { allowedInteractions } = this.state.bookingData?.data ?? {};

        // check is allow to pay
        if (!allowedInteractions?.payment?.deposit && !allowedInteractions?.payment?.full) {
            window.location.href = '#/';
        }

        // set / get booking params
        if (bookingParams !== undefined) {
            this.setState({ bookingParams: bookingParams });
            this.requestStartPayment();
        } else {
            this.getBookingParams();
        }

        this.setState({ pageState: 'loading' });

        this.setActivePaymentMethods();
    }

    setActivePaymentMethods() {
        const { allowedPaymentMethods } = this.state.bookingData?.data ?? {};

        if (allowedPaymentMethods) {
            Object.keys(allowedPaymentMethods).map((method) => {
                let methodType = method.replace('Allowed', '').replace('Sps', '');
                if (params.paymentTypes[methodType] !== undefined) {
                    params.paymentTypes[methodType].allowed = allowedPaymentMethods[method];
                }
                return false;
            });
        }
    }

    setCurrentPaymentStep() {
        checkIsAllowToBookService('paymentDeposit') && checkIsAllowToBookService('paymentOfBalance');

        const priceData = this.state.bookingData.data.frontendParams.price;
        priceData !== undefined && this.setState({ priceData: priceData });

        const fullyPaid = priceData?.fullyPaid;
        const open = priceData?.open;
        const total = priceData?.total;
        const depositAmount = this.state.paymentData?.amountDeposit;

        let paymentStep;
        if (open !== undefined && total !== undefined && fullyPaid !== undefined && fullyPaid === false) {
            if (depositAmount === 0) {
                paymentStep = 'single';
            } else if (total - depositAmount === open) {
                paymentStep = 'final';
            } else {
                paymentStep = 'deposit';
            }
            this.setState({ paymentStep });
        }
    }

    getBookingParams() {
        const loginData = {
            requestType: 'bookingData',
            bookingId: storage.get('bookingNumber'),
            action: 'getBookingParams',
            clientId: 'customerarea',
            requestSource: 'AurumTours-Customerarea',
            hash: storage.get('access_token'),
        };

        axios(buildPostRequest(window.$serviceUrl, '/bookingData', loginData))
            .then((response) => response)
            .then((data) => {
                const response = data.data.data.data;

                if (response) {
                    this.setState({
                        bookingParams: response,
                    });

                    const bookingData = Object.assign(storage.get('bookingData'), {
                        bookingParams: response,
                    });

                    this.requestStartPayment();

                    storage.set('bookingData', bookingData);
                } else {
                    this.setState({ pageState: 'error' });
                }
            })
            .catch((error) => this.setState({ error, pageState: 'error' }));
    }

    changePaymentType(paymentType) {
        this.setState((prevState) => ({
            paymentData: {
                ...prevState.paymentData,
                selected: paymentType,
                paymentValid: false,
            },
            formValid: false,
        }));
    }

    handleCheckboxChange() {
        this.setState({ acceptAgb: !this.state.acceptAgb }, () => {
            this.validateForm();
        });
    }

    validateForm() {
        if (this.state.acceptAgb && this.state.paymentData?.paymentValid) {
            this.setState({ formValid: true });
        } else {
            this.setState({ formValid: false });
        }
    }

    setPaypalInitSuccess(success = true) {
        this.setState({ paypalInitSuccess: success });
    }

    setPaymentData(data) {
        this.setState({
            paymentData: data,
        });
        this.validateForm();
    }

    paymentChange(c) {
        this.setState({
            paymentData: c.paymentData,
            error: false,
        });

        if (c.paymentData !== undefined) {
            if (c.paymentData.paymentValid !== undefined && c.paymentData.paymentValid === true) {
                this.setState({
                    formValid: true,
                });
            } else {
                this.setState({
                    formValid: false,
                });
            }
        }

        if (c.autoSubmit !== undefined && c.autoSubmit === true) {
            this.handleSubmit();
        }

        if (c.requestError === true) {
            this.setState({
                requestError: true,
                errorText: c.errorText !== undefined ? c.errorText : window.$content.errorPaymentText.default,
                isLoading: false,
                formVacancy: false,
            });
        }
    }

    requestStartPayment() {
        const sendData = {
            requestType: 'bookingData',
            action: 'startPayment',
            clientId: 'customerarea',
            requestSource: 'AurumTours-Customerarea',
            hash: storage.get('access_token'),
        };

        axios(buildPostRequest(window.$serviceUrl, '/bookingData', sendData))
            .then((response) => response)
            .then((data) => {
                const response = data.data.data.data;

                if (response) {
                    this.setState(
                        {
                            paymentData: response,
                            pageState: 'loaded',
                            spsUrl: response.spsUrl,
                        },
                        this.setCurrentPaymentStep
                    );
                } else {
                    this.setState({
                        pageState: 'error',
                    });
                    this.paymentChange({ requestError: true, errorText: 'Ein Fehler ist aufgetreten' });
                    if (data.data.data.errorMessage === 'no valid hash given') {
                        return this.context.logout(true);
                    }
                }
            })
            .catch(() => this.paymentChange({ requestError: true }));
    }

    handleSubmit(event) {
        event.preventDefault();

        this.setState({ alreadyTested: true });

        if (!this.state.formValid) {
            return false;
        }

        this.setState({ formVacancy: true });

        const sendParams = {
            requestType: 'bookingData',
            clientId: 'customerarea',
            requestSource: 'AurumTours-Customerarea',
            action: 'updatePayment',
            hash: storage.get('access_token'),
            data: {
                paymentAmount:
                    this.state.paymentStep === 'deposit'
                        ? this.state.paymentData?.amountDeposit
                        : this.state.priceData.open,
            },
        };

        switch (this.state.paymentData?.selected) {
            case 'directDebit':
                Object.assign(sendParams, {
                    paymentData: {
                        paymentDataType: 'directDebit',
                        accountOwner: this.state.paymentData.directDebit.accountOwner,
                        accountNumber: this.state.paymentData.directDebit.accountNumber,
                    },
                });
                this.submitDefault(sendParams);
                break;
            case 'creditCard':
                if (this.state.paymentData?.creditCard !== undefined) {
                    Object.assign(sendParams, {
                        paymentData: {
                            paymentDataType: 'creditCard_sps',
                            accountNumber: '****' + this.state.paymentData.creditCard.accountNumber,
                            accountOwner: this.state.paymentData.creditCard.accountOwner,
                            expirationDate: this.state.paymentData.creditCard.expirationDate,
                            externalContractNumber: this.state.paymentData.creditCard.externalOrderNumber,
                            externalOrderNumber: this.state.paymentData.creditCard.externalOrderNumber,
                            institute: this.state.paymentData.creditCard.institute,
                            securityNumber: this.state.paymentData.creditCard.securityNumber,
                        },
                    });

                    // enable / disable @ Content.js
                    if (window.$3DS2Allowed === 'true') {
                        this.submit3DS2(sendParams);
                    } else {
                        this.submitDefault(sendParams);
                    }
                } else {
                    this.setState({
                        error: true,
                    });
                }
                break;
            case 'payPal':
                Object.assign(sendParams, {
                    paymentData: {
                        paymentDataType: 'paypal_sps',
                        accountOwner: ' ',
                        accountNumber: this.state.paymentData.paypalAccountNumber,
                        externalReferenceNumber: this.state.paymentData.externalReferenceNumber,
                    },
                });

                this.submitDefault(sendParams);

                break;
            default:
        }
    }

    submitDefault(sendParams) {
        sendParams &&
            axios(buildPostRequest(window.$serviceUrl, '/bookingData', sendParams))
                .then((response) => response)
                .then((response) => {
                    if (response.data.data.data.updated === true) {
                        this.setState({
                            paymentDepositSuccess: true,
                            formVacancy: false,
                            pageState: 'success',
                        });

                        // add booked service
                        let bookedService;

                        if (storage.get('bookingData').bookedService === undefined) {
                            bookedService = { bookedService: {} };
                        } else {
                            bookedService = storage.get('bookingData');
                        }

                        bookedService.bookedService['paymentDeposit'] = {
                            data: { success: true },
                        };

                        storage.set('bookingData', Object.assign(storage.get('bookingData'), bookedService));
                    } else {
                        this.setState({
                            formVacancy: false,
                            error: true,
                        });

                        setTimeout(() => {
                            this.setState({
                                error: false,
                            });
                        }, 5000);
                    }
                })
                .catch((error) => this.setState({ error, formVacancy: false }));
    }

    handleCreditCardSuccess(transactionIdentifier) {
        const $this = this;
        const sendParams = {
            requestType: 'bookingData',
            requestSource: 'AurumTours-Customerarea',
            action: 'updatePayment',
            hash: storage.get('access_token'),
            data: {
                paymentAmount:
                    this.state.paymentStep === 'deposit'
                        ? this.state.paymentData?.amountDeposit
                        : this.state.priceData.open,
            },
            paymentData: {
                paymentDataType: 'creditCard_sps',
                accountNumber: '****' + this.state.paymentData.creditCard.accountNumber,
                accountOwner: this.state.paymentData.creditCard.accountOwner,
                expirationDate: this.state.paymentData.creditCard.expirationDate,
                externalOrderNumber: this.state.paymentData.creditCard.externalOrderNumber,
                institute: this.state.paymentData.creditCard.institute,
                securityNumber: this.state.paymentData.creditCard.securityNumber,
                secureIdentifier: transactionIdentifier,
            },
        };

        axios(buildPostRequest(window.$serviceUrl, '/bookingData', sendParams))
            .then((response) => response)
            .then(function (response) {
                if (response.data.data.data.updated === true) {
                    $this.setState({
                        paymentDepositSuccess: true,
                        formVacancy: false,
                        pageState: 'success',
                    });
                } else {
                    $this.setState({
                        formVacancy: false,
                        error: true,
                    });
                }
            })
            .catch((error) => {
                $this.setState({ error, formVacancy: false });
            });
    }

    submit3DS2(sendParams) {
        sendParams = {
            ...sendParams,
            action: 'startSecurePayment',
        };

        axios(buildPostRequest(window.$serviceUrl, '/bookingData', sendParams))
            .then((response) => response)
            .then((data) => {
                const response = data.data.data.data;

                if (response?.secureIdentifier !== undefined) {
                    window.sps.init3dSecure2Session(response.secureIdentifier);
                } else {
                    this.setState({
                        error: true,
                        formVacancy: false,
                    });
                }
            })
            .catch(() => {
                this.setState({
                    error: true,
                    formVacancy: false,
                });
            });

        if (this.state.submitEventAdded === false) {
            // 3DS2Success call -> update payment call

            window.addEventListener('native.3DS2Success', (e) => {
                if (e.detail.transactionIdentifier !== undefined) {
                    const sendParamsDefault = {
                        requestType: 'bookingData',
                        clientId: 'customerarea',
                        requestSource: 'AurumTours-Customerarea',
                        action: 'updatePayment',
                        hash: storage.get('access_token'),
                        data: {
                            paymentAmount:
                                this.state.paymentStep === 'deposit'
                                    ? this.state.paymentData?.amountDeposit
                                    : this.state.priceData.open,
                        },
                        paymentData: {
                            paymentDataType: 'creditCard_sps',
                            accountNumber: '****' + this.state.paymentData.creditCard.accountNumber,
                            accountOwner: this.state.paymentData.creditCard.accountOwner,
                            expirationDate: this.state.paymentData.creditCard.expirationDate,
                            externalOrderNumber: this.state.paymentData.creditCard.externalOrderNumber,
                            institute: this.state.paymentData.creditCard.institute,
                            securityNumber: this.state.paymentData.creditCard.securityNumber,
                            secureIdentifier: e.detail.transactionIdentifier,
                        },
                    };

                    this.submitDefault(sendParamsDefault);
                } else {
                    this.paymentChange({ requestError: true });
                }
            });
            this.setState({ submitEventAdded: true });
        }
    }

    paymentCard(props) {
        const type = props.type;
        const active = this.state.paymentData?.selected === type;

        const cardContent = (type) => {
            if (this.state.spsUrl) {
                if (type === 'creditCard') {
                    return (
                        <>
                            <div className="card-header">
                                <div className="radio-button"></div>
                                <div className="logo-container">
                                    <img
                                        className="logo master-card"
                                        src={masterCardSvg}
                                        width={30}
                                        alt="MasterCard Logo"
                                    />
                                    <img className="logo visa" src={visaSvg} width={40} alt="Visa Logo" />
                                    <img className="logo amex" src={amexSvg} width={25} alt="American Express Logo" />
                                </div>
                                <div className="text-container">
                                    <h4 className="card-headline">Kreditkarte</h4>
                                    <div className="trust-element">
                                        <Icon name="lock" ariaHidden />
                                        Kostenlos und sicher
                                    </div>
                                </div>
                            </div>

                            {active && (
                                <CreditCard
                                    active={active}
                                    valid={this.state.formValid}
                                    spsUrl={this.state.spsUrl}
                                    paymentData={this.state.paymentData}
                                    paymentChange={this.paymentChange}
                                    changePaymentType={this.changePaymentType}
                                    setPaymentData={this.setPaymentData.bind(this)}
                                    handleCreditCardSuccess={this.handleCreditCardSuccess.bind(this)}
                                />
                            )}
                        </>
                    );
                } else if (type === 'directDebit') {
                    return (
                        <>
                            <div className="card-header">
                                <div className="radio-button"></div>
                                <div className="logo-container">
                                    <Icon name="edit" size={26} ariaHidden />
                                    <Icon name="creditCard" size={26} ariaHidden />
                                </div>
                                <div className="text-container">
                                    <h4 className="card-headline">Lastschrift</h4>
                                    <div className="trust-element">
                                        <Icon name="lock" ariaHidden />
                                        Kostenlos und sicher
                                    </div>
                                </div>
                            </div>

                            {active && (
                                <DirectDebit
                                    active={active}
                                    paymentData={this.state.paymentData}
                                    paymentChange={this.paymentChange}
                                    setPaymentData={this.setPaymentData.bind(this)}
                                    alreadyTested={this.state.alreadyTested}
                                />
                            )}
                        </>
                    );
                } else if (type === 'payPal') {
                    return (
                        <>
                            <div className="card-header">
                                <div className="radio-button"></div>
                                <div className="logo-container">
                                    <img className="logo paypal" src={payPalSvg} width={90} alt="PayPal Logo" />
                                </div>
                                <div className="text-container">
                                    <h4 className="card-headline">PayPal</h4>
                                    <div className="trust-element">
                                        <Icon name="lock" ariaHidden />
                                        Kostenlos und sicher
                                    </div>
                                </div>
                            </div>
                            {active && (
                                <PayPal
                                    active={active}
                                    spsUrl={this.state.spsUrl}
                                    setPaymentData={this.setPaymentData.bind(this)}
                                    paymentData={this.state.paymentData}
                                    paymentChange={this.paymentChange}
                                    setPaypalInitSuccess={this.setPaypalInitSuccess.bind(this)}
                                />
                            )}
                        </>
                    );
                }
            }
        };
        const initSuccess = type === 'payPal' && this.state.paypalInitSuccess;

        return (
            <>
                <article
                    className={`card ${params.paymentTypes[type].className}${active ? ' active' : ''}${initSuccess ? ' success' : ''}`}
                    onClick={() => {
                        !active && this.changePaymentType(type);
                        if (type !== 'payPal' && this.state.paypalInitSuccess) {
                            this.setPaypalInitSuccess(false);
                        }
                    }}>
                    <div className="card-content">{cardContent(type, active)}</div>
                </article>
                {active && type === 'creditCard' && this.state.paymentStep === 'deposit' && (
                    <div className="alert credit-card">
                        <Icon name="pendingActions" ariaHidden />
                        Achtung: Wenn Sie mit einer Kreditkarte zahlen, sehen Sie im Zahlungsvorgang den Gesamtpreis
                        Ihrer Reise. Aber tatsächlich wird nur der Anzahlungsbetrag von Ihrer Karte abgebucht.
                    </div>
                )}
                {active && !initSuccess && type === 'payPal' && (
                    <div className="alert paypal">
                        <Icon name="lightbulb" ariaHidden />
                        Tipp: Eine kurze Überprüfung des Zahlungsmittels erleichtert den Zahlungvorgang. Ist das
                        PayPal-Konto (noch) aktiv und das richtige Zahlungsmittel hinterlegt? Sind Zahlungsmittel bzw.
                        das PayPal-Konto ausreichend gedeckt? Kontrollieren Sie Ihre gewählte Option für einen
                        reibungslosen Ablauf.
                    </div>
                )}
            </>
        );
    }

    render() {
        const priceData = this.state.priceData;
        const paymentData = this.state.paymentData;
        const paymentStep = this.state.paymentStep;
        const paymentStepTitle = params.paymentSteps[paymentStep]?.title;

        const paymentSteps = () => {
            const isFinalPayment = paymentStep === 'final';
            const isSinglePayment = paymentStep === 'single';

            return (
                <div className={`open-payment-grid ${isSinglePayment ? 'single-payment' : ''}`}>
                    <div className="payment-dots">
                        {!isSinglePayment && (
                            <div className={`payment-dot ${isFinalPayment ? 'checked' : 'current'}`}>
                                {isFinalPayment && <Icon name="check" className="color-white" ariaHidden />}
                            </div>
                        )}
                        <div className={`payment-dot ${isSinglePayment || isFinalPayment ? 'current' : ''}`}></div>
                    </div>
                    {!isSinglePayment && (
                        <div className="payment-type deposit">
                            <div className="payment-amount">
                                Anzahlung
                                {!!this.state.bookingData.data.allowedInteractions.payment.deposit &&
                                    ` in Höhe von ${convertPrice(paymentData?.amountDeposit)}`}
                            </div>
                            <div className="payment-state">
                                {isFinalPayment ? (
                                    `Bezahlt`
                                ) : (
                                    <>Offen &middot; Fällig spätestens am {paymentData?.depositDate}</>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="payment-type update">
                        <div className="payment-amount">
                            {isSinglePayment ? 'Zahlung' : 'Restzahlung'}{' '}
                            {this.state.bookingData.data.allowedInteractions.payment.deposit
                                ? !!priceData?.open &&
                                  `in Höhe von ${convertPrice(priceData?.open - paymentData?.amountDeposit)}`
                                : !!priceData?.open && `in Höhe von ${convertPrice(priceData?.open)}`}
                        </div>
                        <div className="payment-state">
                            Offen {paymentData?.finalDate && <>&middot; Fällig spätestens am {paymentData.finalDate}</>}
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div className="page-payment">
                <Header />

                {this.state.pageState === 'success' ? (
                    <StatusPage subPage={'payment'} type="success" />
                ) : this.state.pageState === 'error' ? (
                    <StatusPage subPage={'payment'} type="error" />
                ) : (
                    <div className="content-wrapper">
                        <h1 className="main-headline">Zahlungen tätigen / Zahlart ändern</h1>

                        {this.state.pageState === 'loading' && (
                            <div className="wrapper lazy-load-background page-loading">
                                <span className="loading-intro" />
                                <div className="loading-steps">
                                    <span className="loading-steps-line" />
                                    <span className="loading-steps-line" />
                                </div>
                                <div className="loading-steps">
                                    <span className="loading-steps-line" />
                                    <span className="loading-steps-line" />
                                </div>
                                <div className="loading-form">
                                    <span className="loading-form-headline" />
                                    <span className="loading-form-intro" />
                                    <div className="loading-form-payment">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                </div>
                            </div>
                        )}

                        {this.state.pageState === 'loaded' && (
                            <>
                                <section className="intro">
                                    {this.state.bookingData.data.frontendParams.mediatedServices.accommodation &&
                                        this.state.bookingData.data.frontendParams.postPayServices.accommodation && (
                                            <section className="info-section">
                                                <article>
                                                    <p>
                                                        Die angegebenen Zahldaten beziehen sich auf sämtliche an
                                                        AurumTours zu entrichtenden Leistungen. Die Bezahlung der
                                                        vermittelten Hotelleistung erfolgt gemäß den jeweiligen
                                                        Buchungsbedingungen direkt an das Hotel.
                                                    </p>
                                                </article>
                                            </section>
                                        )}
                                    {this.context.travelType === 'cruise' &&
                                        this.context.bookingData.data.frontendParams.mediatedServices.cruise && (
                                            <section className="info-section">
                                                <article>
                                                    <p>
                                                        Die angegebenen Zahldaten beziehen sich auf sämtliche an
                                                        AurumTours zu entrichtenden Leistungen. Die Bezahlung der
                                                        vermittelten Kreuzfahrtleistung erfolgt gemäß den jeweiligen
                                                        Buchungsbedingungen direkt an die Reederei.
                                                    </p>
                                                </article>
                                            </section>
                                        )}
                                    <div className="subline">Für Ihre Reise sind folgende Zahlungen noch offen:</div>
                                    {paymentSteps()}
                                </section>
                                <form
                                    onSubmit={(e) => this.handleSubmit(e)}
                                    onChange={() => {
                                        tracking('interaction', 'payment');
                                        this.setState({ error: false });
                                    }}>
                                    <section
                                        className={`main-content wrapper ${
                                            this.state.paymentData?.paymentValid && this.state.formVacancy && 'disabled'
                                        }`}>
                                        <h2 className="section-headline">{paymentStepTitle} tätigen</h2>
                                        <p>
                                            Bitte wählen Sie eine Zahlart, um Ihre {paymentStepTitle} zu tätigen.{' '}
                                            {paymentStepTitle === 'Anzahlung'
                                                ? 'Diese wird dann auch für die Restzahlung verwendet, wenn diese fällig ist.'
                                                : 'Diese wird dann auch für künftige Zahlungen verwendet, sollten Sie weitere Zusatzleistungen zu Ihrer Reise hinzubuchen.'}
                                        </p>

                                        {Object.keys(params.paymentTypes).filter(
                                            (type) => params.paymentTypes[type].allowed
                                        ).length > 0 ? (
                                            <>
                                                {Object.keys(params.paymentTypes).map((type) => {
                                                    if (params.paymentTypes[type].allowed) {
                                                        return <this.paymentCard type={type} key={type} />;
                                                    }
                                                    return false;
                                                })}
                                            </>
                                        ) : (
                                            <InfoCard>{paymentAlertText}</InfoCard>
                                        )}
                                        {this.state.alreadyTested && !this.state.paymentData?.selected && (
                                            <div className="text-red error">Bitte wählen Sie eine Zahlart aus.</div>
                                        )}
                                    </section>

                                    <section className="accept-section wrapper">
                                        <div className="checkbox-input">
                                            <input
                                                type="checkbox"
                                                name="accept-payment"
                                                id="accept-payment"
                                                checked={this.state.acceptAgb}
                                                onChange={this.handleCheckboxChange}
                                            />
                                            <span className="icon" onClick={this.handleCheckboxChange}></span>
                                            <div>
                                                <label htmlFor="accept-payment">
                                                    Mit dem Vorgang "Zahldaten übermitteln" bestätige ich, dass ich alle
                                                    oben stehenden Daten und Eingaben überprüft habe und die Richtigkeit
                                                    meiner Angaben vorliegt. Durch die Übermittlung meiner Zahldaten
                                                    stimme ich zu, dass zukünftige Zahlungen ebenfalls über das von mir
                                                    angegebene Zahlmittel abgewickelt werden. Die{' '}
                                                    <a
                                                        href={`${window.$website}/unternehmen/agb`}
                                                        rel="noopener noreferrer"
                                                        target="_blank">
                                                        AGBs
                                                    </a>{' '}
                                                    und{' '}
                                                    <NavLink to="/datenschutz" target="_blank">
                                                        Datenschutzhinweise
                                                    </NavLink>{' '}
                                                    von AurumTours habe ich gelesen und akzeptiere diese.
                                                </label>
                                                {this.state.alreadyTested && !this.state.acceptAgb && (
                                                    <div className="text-red error">
                                                        Um die Zahlung durchführen zu können, müssen Sie den Bedingungen
                                                        zustimmen.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </section>

                                    <section className="user-action wrapper">
                                        <h3 className="price-title">
                                            Zahlbetrag
                                            {this.state.paymentStep !== 'single' && <>{` (${paymentStepTitle})`}</>}
                                        </h3>
                                        <div className="price-value">
                                            {this.state.bookingData.data.allowedInteractions.payment.deposit
                                                ? convertPrice(paymentData?.amountDeposit)
                                                : convertPrice(priceData?.open)}
                                        </div>
                                        <small className="price-fineprint">
                                            {paymentStepTitle}sbetrag für alle Reisenden
                                        </small>
                                        <Button
                                            type="submit"
                                            valid={this.state.formValid}
                                            validated={this.state.formVacancy}
                                            icon="arrowForward"
                                            label={
                                                !this.state.formVacancy
                                                    ? 'Zahldaten übermitteln'
                                                    : 'Zahldaten werden übermittelt'
                                            }
                                        />
                                        {this.state.error && (
                                            <p className="text-red error">
                                                Ein Fehler ist aufgetreten. Bitte überprüfen Sie Ihre Zahldaten.
                                            </p>
                                        )}
                                    </section>
                                </form>
                            </>
                        )}
                    </div>
                )}

                <Footer />
            </div>
        );
    }
}

export default Index;
